import React from "react";

/**
 * Interface for the TabNavigation props
 */
interface TabNavigationProps {
    tabs: Array<{
        id: 'cough' | 'clear';
        label: React.ReactNode;
    }>;
    activeTab: string;
    onTabChange: (tabId: 'cough' | 'clear') => void;
    className?: string;
    styles?: any;
}

/**
 * TabNavigation - A reusable component for tab-based navigation
 * 
 * @param {Object[]} tabs - Array of tab objects with id and label
 * @param {string} activeTab - ID of the currently active tab
 * @param {function} onTabChange - Callback function when tab changes
 * @param {string} className - Additional CSS classes for the container
 * @param {Object} styles - Custom styles for different parts of the component
 * @returns {React.Component} Tab navigation component
 */
const TabNavigation = ({
    tabs,
    activeTab,
    onTabChange,
    className = "",
    styles = {}
}: TabNavigationProps) => {
    // Default styles that can be overridden
    const defaultStyles = {
        container: "flex w-full border-b mb-4",
        tab: "w-1/2 pb-2 font-semibold text-[16px] text-center font-medium cursor-pointer transition-colors",
        activeTab: "border-b-2 border-cyan-500 text-black-500",
        inactiveTab: "text-cyan-600",
        title: "w-[121px] h-[20px] font-[Figtree] leading-[20px] tracking-[0.374px] text-black flex-none order-0 grow-0"
    };

    // Merge default styles with custom styles
    const mergedStyles = {
        container: `${defaultStyles.container} ${styles.container || ""}`,
        tab: `${defaultStyles.tab} ${styles.tab || ""}`,
        activeTab: `${defaultStyles.activeTab} ${styles.activeTab || ""}`,
        inactiveTab: `${defaultStyles.inactiveTab} ${styles.inactiveTab || ""}`
    };

    return (
        <div className={`${mergedStyles.container} ${className}`} role="tablist">
            {tabs.map((tab) => (
                <div
                    key={tab.id}
                    className={`${mergedStyles.tab} ${activeTab === tab.id ? mergedStyles.activeTab : mergedStyles.inactiveTab
                        }`}
                    onClick={() => onTabChange(tab.id)}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    tabIndex={0}
                >
                    {tab.label}
                </div>
            ))}
        </div>
    );
};

// TypeScript provides type checking via TabNavigationProps, so no need for PropTypes.

export default TabNavigation;